import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, ChevronUp, Copy, FileText, Minus, MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import { parseVendorAccount } from "./vendorDisplay";

export const PAYMENT_TERMS = [
    { key: "depositFirst", label: "D1" },
    { key: "depositSecond", label: "D2" },
    { key: "balance", label: "Bal" },
    { key: "credit", label: "Cr" },
];

export const NET_TERMS = [
    { key: "depositFirstDays", label: "D1" },
    { key: "depositSecondDays", label: "D2" },
    { key: "balanceDays", label: "Bal" },
    { key: "creditDays", label: "Cr" },
    { key: "shippingDays", label: "Ship" },
];

export const parseEmails = (emails) => {
    if (!emails) return [];
    return emails.split(",").map((email) => email.trim()).filter(Boolean);
};

const Dash = () => <span className="text-gray-400">—</span>;

export const TermValues = ({ terms, vendor, suffix, compact = false }) => {
    const activeTerms = terms.filter(({ key }) => Number(vendor[key]) > 0);

    if (!activeTerms.length) {
        return <Dash />;
    }

    return (
        <div className={`inline-flex gap-2.5 ${compact ? "flex-wrap whitespace-normal" : "whitespace-nowrap"}`}>
            {activeTerms.map(({ key, label }) => (
                <span key={key} className={`inline-flex items-center gap-1 ${compact ? "text-xs" : "text-[13px]"} whitespace-nowrap`}>
                    <span className="text-gray-500">{label}</span>
                    <span className="font-semibold text-gray-900 tabular-nums">{vendor[key]}{suffix}</span>
                </span>
            ))}
        </div>
    );
};

const STATUS_PILL_CLASS = {
    Active: "bg-emerald-50 text-emerald-700 border-emerald-200",
    Inactive: "bg-gray-100 text-gray-700 border-gray-200",
    Test: "bg-amber-50 text-amber-700 border-amber-200",
};

export const StatusPill = ({ status }) => (
    <span
        className={`inline-flex items-center whitespace-nowrap rounded-md border px-2 py-0.5 text-xs font-medium leading-5 ${STATUS_PILL_CLASS[status] || STATUS_PILL_CLASS.Inactive}`}
    >
        {status}
    </span>
);

const RowActionsMenu = ({ vendor, onCopyVendorId, onViewProducts, onEditVendor, onDeleteVendor }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [menuStyle, setMenuStyle] = useState(null);
    const buttonRef = useRef(null);
    const menuRef = useRef(null);

    useEffect(() => {
        if (!isOpen) return;

        const updatePosition = () => {
            if (!buttonRef.current) return;
            const rect = buttonRef.current.getBoundingClientRect();
            setMenuStyle({
                position: "fixed",
                top: rect.bottom + 4,
                right: window.innerWidth - rect.right,
                zIndex: 100,
            });
        };
        const handleClickOutside = (event) => {
            if (buttonRef.current?.contains(event.target) || menuRef.current?.contains(event.target)) return;
            setIsOpen(false);
        };

        const close = () => setIsOpen(false);

        updatePosition();
        document.addEventListener("mousedown", handleClickOutside);
        window.addEventListener("resize", updatePosition);
        window.addEventListener("scroll", close, true);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            window.removeEventListener("resize", updatePosition);
            window.removeEventListener("scroll", close, true);
        };
    }, [isOpen]);

    const items = [
        { label: "Copy vendor ID", icon: Copy, onSelect: () => onCopyVendorId?.(vendor.vendorId) },
        { label: "View products", icon: FileText, onSelect: () => onViewProducts?.(vendor) },
        { label: "Edit vendor", icon: Pencil, onSelect: () => onEditVendor?.(vendor) },
        { label: "Delete vendor", icon: Trash2, onSelect: () => onDeleteVendor?.(vendor.id), danger: true },
    ];

    return (
        <>
            <button
                ref={buttonRef}
                type="button"
                title="Actions"
                aria-haspopup="menu"
                aria-expanded={isOpen}
                className="rounded-md p-1 text-gray-700 hover:bg-gray-200 transition-colors"
                onClick={(e) => {
                    e.stopPropagation();
                    setIsOpen((open) => !open);
                }}
            >
                <MoreHorizontal className="w-5 h-5" />
            </button>
            {isOpen && createPortal(
                <div
                    ref={menuRef}
                    role="menu"
                    className="min-w-[170px] overflow-hidden rounded-md border border-border bg-white p-1 shadow-md"
                    style={menuStyle || { position: "fixed", left: -9999, top: -9999, visibility: "hidden" }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {items.map(({ label, icon: Icon, onSelect, danger }) => (
                        <button
                            key={label}
                            type="button"
                            role="menuitem"
                            className={`flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-[14px] hover:bg-gray-100 ${danger ? "text-red-600" : "text-foreground"}`}
                            onClick={() => {
                                setIsOpen(false);
                                onSelect();
                            }}
                        >
                            <Icon className="w-3.5 h-3.5" />
                            {label}
                        </button>
                    ))}
                </div>,
                document.body
            )}
        </>
    );
};

const HEADER_CELL_CLASS = "sticky top-0 z-10 px-3 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-gray-900 bg-gray-100 select-none whitespace-nowrap shadow-[rgb(229,231,235)_0px_1px_0px_0px]";
const SORTABLE_HEADER_CELL_CLASS = `${HEADER_CELL_CLASS} cursor-pointer hover:text-gray-600 transition-colors`;
const CELL_CLASS = "px-3 py-2.5 text-sm whitespace-nowrap align-middle border-t border-border";

const SortableHeader = ({ columnId, label, sorting, onSort }) => {
    const isActive = sorting?.id === columnId;

    return (
        <th
            className={SORTABLE_HEADER_CELL_CLASS}
            onClick={() => onSort?.(columnId)}
        >
            <div className="flex items-center gap-1">
                {label}
                {isActive && (
                    sorting.desc
                        ? <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
                        : <ChevronUp className="w-3.5 h-3.5 text-gray-400" />
                )}
            </div>
        </th>
    );
};

const VendorCheckbox = ({
    checked,
    indeterminate = false,
    onChange,
    onClick,
    ariaLabel,
}) => (
    <label
        className="relative inline-flex cursor-pointer"
        onClick={onClick}
    >
        <input
            type="checkbox"
            className="sr-only"
            checked={checked}
            aria-label={ariaLabel}
            ref={(el) => {
                if (el) el.indeterminate = indeterminate;
            }}
            onChange={onChange}
        />
        <span
            className={`size-4 shrink-0 rounded-[4px] border shadow-xs translate-y-[1px] flex items-center justify-center transition-colors ${
                checked || indeterminate
                    ? "bg-black border-black"
                    : "bg-white border-input"
            }`}
        >
            {checked && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
            {indeterminate && !checked && <Minus className="w-3 h-3 text-white" strokeWidth={3} />}
        </span>
    </label>
);

const VendorTable = ({
    vendors = [],
    selectedIds = [],
    visibleColumns = {},
    onToggleSelect,
    onToggleSelectAll,
    onDeleteVendor,
    onCopyVendorId,
    onEditVendor,
    onViewProducts,
    onRowClick,
    sorting,
    onSort,
}) => {
    const allSelected = vendors.length > 0 && selectedIds.length === vendors.length;
    const someSelected = selectedIds.length > 0 && !allSelected;
    const showColumn = (columnId) => visibleColumns[columnId] !== false;

    return (
        <table className="w-full border-separate border-spacing-0 text-sm">
            <thead>
                <tr>
                    <th className="sticky top-0 z-10 w-10 pl-4 pr-2 py-2.5 text-left bg-gray-100 shadow-[rgb(229,231,235)_0px_1px_0px_0px]">
                        <VendorCheckbox
                            checked={allSelected}
                            indeterminate={someSelected}
                            onChange={onToggleSelectAll}
                            ariaLabel="Select all vendors"
                        />
                    </th>
                    <SortableHeader columnId="name" label="Vendor" sorting={sorting} onSort={onSort} />
                    {showColumn("emails") && (
                        <SortableHeader columnId="emails" label="Email(s)" sorting={sorting} onSort={onSort} />
                    )}
                    {showColumn("account") && (
                        <SortableHeader columnId="accountNumber" label="Account" sorting={sorting} onSort={onSort} />
                    )}
                    {showColumn("category") && (
                        <th className={HEADER_CELL_CLASS}>Category</th>
                    )}
                    {showColumn("paymentTerms") && (
                        <th className={HEADER_CELL_CLASS}>Payment Terms</th>
                    )}
                    {showColumn("netTerms") && (
                        <th className={HEADER_CELL_CLASS}>Net Terms</th>
                    )}
                    {showColumn("status") && (
                        <th className={HEADER_CELL_CLASS}>Status</th>
                    )}
                    <th className={`${HEADER_CELL_CLASS} text-right`}>Actions</th>
                </tr>
            </thead>
            <tbody>
                {vendors.length === 0 && (
                    <tr>
                        <td colSpan={99} className="px-4 py-10 text-center text-sm text-gray-500">
                            No vendors match the current filters.
                        </td>
                    </tr>
                )}
                {vendors.map((vendor) => {
                    const emailList = parseEmails(vendor.emails);
                    const isSelected = selectedIds.includes(vendor.id);
                    const { category, accountNumber, status } = parseVendorAccount(vendor);

                    return (
                        <tr
                            key={vendor.id}
                            className={`transition-colors duration-100 hover:bg-gray-50 cursor-pointer ${isSelected ? "bg-gray-50/80" : ""}`}
                            onClick={() => onRowClick?.(vendor)}
                        >
                            <td className={CELL_CLASS}>
                                <VendorCheckbox
                                    checked={isSelected}
                                    onChange={() => onToggleSelect(vendor.id)}
                                    onClick={(e) => e.stopPropagation()}
                                    ariaLabel={`Select ${vendor.name}`}
                                />
                            </td>
                            <td className="px-3 py-2.5 text-sm align-middle whitespace-normal min-w-[200px] border-t border-border">
                                <div className="font-medium text-gray-900">{vendor.name || "—"}</div>
                                <div className="text-xs text-muted-foreground">ID: {vendor.vendorId || "—"}</div>
                            </td>
                            {showColumn("emails") && (
                                <td className={CELL_CLASS}>
                                    {emailList.length ? (
                                        <>
                                            <span className="text-gray-600">{emailList[0]}</span>
                                            {emailList.length > 1 && (
                                                <span className="ml-1.5 text-xs text-muted-foreground" title={emailList.slice(1).join(", ")}>
                                                    +{emailList.length - 1} more
                                                </span>
                                            )}
                                        </>
                                    ) : (
                                        <Dash />
                                    )}
                                </td>
                            )}
                            {showColumn("account") && (
                                <td className={`${CELL_CLASS} text-gray-600 tabular-nums`}>
                                    {accountNumber || <Dash />}
                                </td>
                            )}
                            {showColumn("category") && (
                                <td className={`${CELL_CLASS} text-gray-700`}>
                                    {category || <Dash />}
                                </td>
                            )}
                            {showColumn("paymentTerms") && (
                                <td className={CELL_CLASS}>
                                    <TermValues terms={PAYMENT_TERMS} vendor={vendor} suffix="%" />
                                </td>
                            )}
                            {showColumn("netTerms") && (
                                <td className={CELL_CLASS}>
                                    <TermValues terms={NET_TERMS} vendor={vendor} suffix="d" />
                                </td>
                            )}
                            {showColumn("status") && (
                                <td className={CELL_CLASS}>
                                    <StatusPill status={status} />
                                </td>
                            )}
                            <td className={`${CELL_CLASS} text-right`}>
                                <RowActionsMenu
                                    vendor={vendor}
                                    onCopyVendorId={onCopyVendorId}
                                    onViewProducts={onViewProducts}
                                    onEditVendor={onEditVendor}
                                    onDeleteVendor={onDeleteVendor}
                                />
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
};

export default VendorTable;
